import { useEffect, useState } from "react";
import { ExternalLink, Newspaper } from "lucide-react";
import api from "@/lib/api";

const TIME_AGO = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const isLive = (iso) => {
  if (!iso) return false;
  return (Date.now() - new Date(iso).getTime()) / 1000 < 15 * 60; // < 15 min old
};

const SOURCE_COLORS = {
  "BBC Sport": "#FF3232",
  "Sky Sports": "#00B6FF",
  "The Guardian": "#FFD200",
  "Goal.com": "#FF6B00",
  ESPN: "#D80000",
};

// Compact 2-up news strip. Always shows the 2 newest articles from cache.
export default function NewsStrip({ testId = "news-strip" }) {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api
      .get("/news")
      .then((r) => setArticles(r.data?.articles || []))
      .catch(() => {});
  }, []);

  if (!articles.length) return null;

  const visible = articles.slice(0, 2);

  return (
    <section data-testid={testId} className="mt-8 sb-fade-up">
      <div className="flex items-center justify-between mb-2 px-1">
        <h2
          className="font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2"
          style={{ color: "var(--sb-text-muted)" }}
        >
          <Newspaper size={12} /> // Sports News
        </h2>
        <span className="font-mono text-[10px]" style={{ color: "var(--sb-text-muted)" }}>
          AUTO-REFRESHES EVERY 30 MIN
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {visible.map((a, i) => (
          <a
            key={a.link}
            href={a.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testId}-card-${i}`}
            className="group sb-card rounded-lg overflow-hidden flex"
            style={{ minHeight: 96 }}
          >
            {a.image ? (
              <div
                className="flex-shrink-0 relative overflow-hidden"
                style={{ width: 120, background: "var(--sb-surface-hover)" }}
              >
                <img
                  src={a.image}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            ) : (
              <div
                className="flex-shrink-0 flex items-center justify-center"
                style={{ width: 120, background: "var(--sb-surface-hover)" }}
              >
                <Newspaper size={28} color="var(--sb-text-muted)" />
              </div>
            )}

            <div className="flex-1 min-w-0 p-3 flex flex-col justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                  style={{
                    color: SOURCE_COLORS[a.source] || "var(--sb-primary)",
                    border: `1px solid ${SOURCE_COLORS[a.source] || "var(--sb-primary)"}55`,
                    background: `${SOURCE_COLORS[a.source] || "#3D7EFF"}14`,
                  }}
                >
                  {a.source}
                </span>
                {isLive(a.published_at) && (
                  <span
                    data-testid={`${testId}-live-${i}`}
                    className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1"
                    style={{
                      color: "#FF3232",
                      border: "1px solid rgba(255,50,50,0.45)",
                      background: "rgba(255,50,50,0.10)",
                    }}
                  >
                    <span
                      className="ns-live-dot inline-block rounded-full"
                      style={{ width: 6, height: 6, background: "#FF3232" }}
                    />
                    LIVE
                  </span>
                )}
                <span
                  className="font-mono text-[10px]"
                  style={{ color: "var(--sb-text-muted)" }}
                >
                  {TIME_AGO(a.published_at)}
                </span>
              </div>

              <p
                className="font-display font-bold text-sm leading-snug"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {a.title}
              </p>

              <span
                className="font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1 self-start"
                style={{ color: "var(--sb-primary)" }}
              >
                Read <ExternalLink size={10} />
              </span>
            </div>
          </a>
        ))}
      </div>

      <style>{`
        .ns-live-dot {
          box-shadow: 0 0 0 0 rgba(255, 50, 50, 0.7);
          animation: ns-live-pulse 1.4s ease-out infinite;
        }
        @keyframes ns-live-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(255, 50, 50, 0.7); transform: scale(1); }
          70%  { box-shadow: 0 0 0 6px rgba(255, 50, 50, 0);   transform: scale(1.18); }
          100% { box-shadow: 0 0 0 0   rgba(255, 50, 50, 0);   transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ns-live-dot { animation: none; }
        }
      `}</style>
    </section>
  );
}
